import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/Axios";

function AdminTeamMembers() {
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    nameUrdu: '',
    title: '',
    titleUrdu: '',
    image: '',
    bio: '',
    bioUrdu: '',
    email: '',
    phone: '',
    order: 0,
    isActive: true
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("adminToken");
      if (!token) {
        navigate("/admin/login");
        return;
      }

      const response = await api.get("/admin/team-members/all", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamMembers(response.data.teamMembers || []);
    } catch (error) {
      console.error("Fetch team members error:", error);
      toast.error("Team members لوڈ کرنے میں ناکام");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value)
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      nameUrdu: '',
      title: '',
      titleUrdu: '',
      image: '',
      bio: '',
      bioUrdu: '',
      email: '',
      phone: '',
      order: 0,
      isActive: true
    });
    setEditingMember(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("adminToken");
      const payload = {
        name: formData.name,
        nameUrdu: formData.nameUrdu,
        title: formData.title,
        titleUrdu: formData.titleUrdu,
        image: formData.image,
        bio: formData.bio,
        bioUrdu: formData.bioUrdu,
        email: formData.email,
        phone: formData.phone,
        order: formData.order,
        isActive: formData.isActive
      };

      if (editingMember) {
        await api.put(`/admin/team-members/${editingMember._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Team member اپڈیٹ ہو گیا");
      } else {
        await api.post("/admin/team-members", payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Team member شامل ہو گیا");
      }

      setShowAddModal(false);
      resetForm();
      fetchTeamMembers();
    } catch (error) {
      console.error("Save team member error:", error);
      toast.error(error.response?.data?.message || "Team member محفوظ کرنے میں ناکام");
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name || '',
      nameUrdu: member.nameUrdu || '',
      title: member.title || '',
      titleUrdu: member.titleUrdu || '',
      image: member.image || '',
      bio: member.bio || '',
      bioUrdu: member.bioUrdu || '',
      email: member.email || '',
      phone: member.phone || '',
      order: member.order || 0,
      isActive: member.isActive !== undefined ? member.isActive : true
    });
    setShowAddModal(true);
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('کیا آپ واقعی یہ team member حذف کرنا چاہتے ہیں؟')) {
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      await api.delete(`/admin/team-members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Team member حذف ہو گیا");
      fetchTeamMembers();
    } catch (error) {
      console.error("Delete team member error:", error);
      toast.error("Team member حذف کرنے میں ناکام");
    }
  };

  return (
    <>
      <Helmet><title>Team Members Management - Admin Panel</title></Helmet>
      
      <div className="min-h-screen bg-brand-background">
        {/* Mobile Menu Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}

        <div className="flex flex-col md:flex-row">
          {/* Sidebar - Fixed */}
          <aside className={`fixed top-0 left-0 h-screen w-64 bg-white shadow-md border-r border-brand-card/60 z-40 overflow-y-auto transform transition-transform duration-300 ease-in-out ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
          }`}>
            <div className="p-4 border-b border-brand-card/40 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-brand-header">Admin Navigation</h2>
                <p className="text-xs text-gray-500 mt-1">Quick access to tools</p>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="md:hidden text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <nav className="p-3 space-y-2 text-sm">
              <button
                onClick={() => {
                  navigate('/admin');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Dashboard</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/bills');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Bill Management</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/fields');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Field Settings</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/roznamacha-fields');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Roz Namacha Fields</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/recoveries');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Recovery Management</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/expenses');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Expense Management</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/payment-methods');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Payment Methods</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/team-members');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Team Members</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/recognitions');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Recognitions</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/blogs');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Blogs</span>
              </button>
              <button
                onClick={() => {
                  navigate('/company/dashboard');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Company Dashboard</span>
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem("adminToken");
                  localStorage.removeItem("adminData");
                  navigate("/admin/login");
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Logout</span>
              </button>
            </nav>
          </aside>

          {/* Main Content */}
          <div className="flex-1 md:ml-64 p-4 md:p-8">
            {/* Mobile Header with Menu Button */}
            <div className="md:hidden mb-4 flex items-center justify-between bg-white rounded-lg shadow-md p-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="text-gray-600 hover:text-gray-900"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-xl font-bold text-gray-900">Team Members</h1>
              <div className="w-6"></div>
            </div>

            {/* Header */}
            <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6 border border-brand-card/60">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-brand-header">Team Members Management</h1>
                  <p className="text-gray-600 mt-1 text-sm md:text-base">Manage team members for the website</p>
                </div>
                <button
                  onClick={() => {
                    resetForm();
                    setShowAddModal(true);
                  }}
                  className="bg-brand-header hover:bg-emerald-800 text-white font-semibold px-4 md:px-6 py-2 md:py-3 rounded-lg transition transform hover:-translate-y-0.5 hover:shadow-lg text-sm md:text-base"
                >
                  + Add Team Member
                </button>
              </div>
            </div>

            {/* Team Members List */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden border border-brand-card/60">
              {isLoading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-header mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading team members...</p>
                </div>
              ) : teamMembers.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-gray-600">No team members found. Add your first team member!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-brand-background">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Image
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Phone
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Order
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {teamMembers.map((member) => (
                        <tr key={member._id} className="hover:bg-brand-background/60 transition">
                          <td className="px-6 py-4 whitespace-nowrap">
                            {member.image ? (
                              <img src={member.image} alt={member.name} className="h-12 w-12 rounded-full object-cover" />
                            ) : (
                              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-brand-header to-green-600 flex items-center justify-center text-white text-lg font-bold">
                                {member.name.charAt(0)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{member.name}</div>
                            {member.nameUrdu && (
                              <div className="text-sm text-gray-500">{member.nameUrdu}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{member.title}</div>
                            {member.titleUrdu && (
                              <div className="text-sm text-gray-500">{member.titleUrdu}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{member.email || '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{member.phone || '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{member.order}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              member.isActive
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : "bg-red-50 text-brand-alert border border-red-200"
                            }`}>
                              {member.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => handleEdit(member)}
                              className="text-indigo-600 hover:text-indigo-900 mr-4"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(member._id)}
                              className="text-brand-alert hover:text-red-900"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 my-8">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingMember ? 'Edit Team Member' : 'Add New Team Member'}
                </h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name (English) *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name (Urdu)
                    </label>
                    <input
                      type="text"
                      name="nameUrdu"
                      value={formData.nameUrdu}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="جان ڈو"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title (English) *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="Project Manager"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title (Urdu)
                    </label>
                    <input
                      type="text"
                      name="titleUrdu"
                      value={formData.titleUrdu}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="پروجیکٹ مینیجر"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image URL
                  </label>
                  <input
                    type="url"
                    name="image"
                    value={formData.image}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bio (English)
                    </label>
                    <textarea
                      name="bio"
                      value={formData.bio}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="Team member bio..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bio (Urdu)
                    </label>
                    <textarea
                      name="bioUrdu"
                      value={formData.bioUrdu}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="ٹیم ممبر کی تفصیل..."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="+92 300 1234567"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Order
                    </label>
                    <input
                      type="number"
                      name="order"
                      value={formData.order}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="0"
                    />
                  </div>
                  <div className="flex items-center pt-8">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition"
                  >
                    {editingMember ? 'Update' : 'Add'} Team Member
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default AdminTeamMembers;

